import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { LayoutGrid, Map as MapIcon, MapPinPlus, Users, Camera } from "lucide-react";
import { useFetch } from "@/lib/hooks";
import { fdate } from "@/lib/api";
import { PageHeader, StatusBadge, Loading, Empty, DemoBadge, LiveIndicator } from "@/components/common";
import { GeoMap } from "@/components/GeoMap";
import { SocialBar, CommentCount } from "@/components/community";

export default function Community() {
  const navigate = useNavigate();
  const cats = useFetch("/meta/catalogs");
  const [mun, setMun] = useState("all");
  const [cat, setCat] = useState("all");
  const [status, setStatus] = useState("abiertos");
  const [sort, setSort] = useState("votos");
  const [view, setView] = useState("grid");
  const path = useMemo(() => {
    const p = new URLSearchParams({ sort, status });
    if (mun !== "all") p.set("municipio", mun);
    if (cat !== "all") p.set("category", cat);
    return `/reports/public?${p}`;
  }, [mun, cat, status, sort]);
  const feed = useFetch(path, { poll: 15000 });
  const patch = (folio, s) => feed.setData((d) => d.map((r) => (r.folio === folio ? { ...r, ...s } : r)));

  return (
    <div>
      <PageHeader eyebrow="Comunidad" title="Lo que reportan tus vecinos"
        subtitle="Reportes públicos de la ciudad. Vota los que también te afectan, síguelos para recibir cada avance y suma comentarios o fotos."
        actions={<><LiveIndicator lastSync={feed.lastSync} /><Button onClick={() => navigate("/app/reportar")} className="btn-press" data-testid="community-new-report-button"><MapPinPlus className="h-4 w-4 mr-1.5" />Reportar</Button></>} />
      <Card className="mb-4"><CardContent className="p-3 flex flex-wrap gap-2 items-center">
        <Select value={mun} onValueChange={setMun}>
          <SelectTrigger className="w-[220px]" data-testid="community-municipio-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todo Quintana Roo</SelectItem>{(cats.data?.municipios || []).map((m) => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-[200px]" data-testid="community-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todas las categorías</SelectItem>{(cats.data?.categories || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        <ToggleGroup type="single" size="sm" value={status} onValueChange={(v) => v && setStatus(v)}>
          <ToggleGroupItem value="abiertos" data-testid="community-status-abiertos">Abiertos</ToggleGroupItem>
          <ToggleGroupItem value="resueltos" data-testid="community-status-resueltos">Resueltos</ToggleGroupItem>
          <ToggleGroupItem value="todos" data-testid="community-status-todos">Todos</ToggleGroupItem>
        </ToggleGroup>
        <ToggleGroup type="single" size="sm" value={sort} onValueChange={(v) => v && setSort(v)}>
          <ToggleGroupItem value="votos" data-testid="community-sort-votos">Más votados</ToggleGroupItem>
          <ToggleGroupItem value="recientes" data-testid="community-sort-recientes">Recientes</ToggleGroupItem>
        </ToggleGroup>
        <ToggleGroup type="single" size="sm" value={view} onValueChange={(v) => v && setView(v)} className="ml-auto">
          <ToggleGroupItem value="grid" aria-label="Tarjetas" data-testid="community-view-grid"><LayoutGrid className="h-4 w-4" /></ToggleGroupItem>
          <ToggleGroupItem value="map" aria-label="Mapa" data-testid="community-view-map"><MapIcon className="h-4 w-4" /></ToggleGroupItem>
        </ToggleGroup>
      </CardContent></Card>

      {feed.loading ? <Loading rows={4} /> : !feed.data?.length ? (
        <Card><CardContent><Empty icon={Users} title="No hay reportes públicos con estos filtros" hint="Cuando crees un reporte, actívalo como público para que tus vecinos lo voten." /></CardContent></Card>
      ) : view === "map" ? (
        <Card><CardContent className="p-3"><GeoMap className="h-[600px]" testid="community-map" title="Comunidad · en vivo" reports={feed.data || []} onSelectReport={(r) => navigate(`/app/reportes/${r.folio}`)} hexData={feed.data || []} defaultLayers={{ hex: false }} /></CardContent></Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="community-feed">
          {feed.data.map((r) => (
            <Card key={r.folio} className="card-hover cursor-pointer overflow-hidden flex flex-col" onClick={() => navigate(`/app/reportes/${r.folio}`)} data-testid={`community-card-${r.folio}`}>
              {r.photo_thumb ? <img src={r.photo_thumb} alt="evidencia" className="h-36 w-full object-cover" />
                : <div className="h-2 bg-[#DFF3F6]" />}
              <CardContent className="p-4 flex flex-col gap-2 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium">{r.category_label} {r.is_demo && <DemoBadge />}</div>
                    <div className="text-xs text-muted-foreground">{r.colonia ? `${r.colonia} · ` : ""}{r.municipio}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <p className="text-sm text-foreground/80 line-clamp-2">{r.description}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-auto pt-1">
                  <span className="font-mono">{r.folio}</span><span>{r.author}{r.mine ? " (tú)" : ""}</span><span>{fdate(r.created_at)}</span>
                  <span className="ml-auto flex items-center gap-2">{r.has_photo && <Camera className="h-3.5 w-3.5" />}<CommentCount n={r.comment_count} /></span>
                </div>
                <SocialBar compact folio={r.folio} state={r} onChange={(s) => patch(r.folio, s)} />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
